import * as THREE from "three";
import { WORLD_SIZE } from "@zend/content";
import type { SceneHandle } from "../scene/renderer.js";
import { buildingTier } from "../assets/manifest.js";
import { InstancedModel } from "../base/sceneParts.js";
import { serverNow } from "../state/serverTime.js";
import type { MarchView, WorldMe, WorldTile, WorldView } from "./types.js";
import { MarchTrail } from "./MarchTrail.js";
import { MarchColumn, type MarchModelState } from "./MarchColumn.js";
import { marchLeg } from "./marchPresentation.js";
import { WorldTerrain } from "./WorldTerrain.js";
import { useSession } from "../state/session.js";
import { WorldFx } from "./WorldFx.js";
import { WorldBoss, WORLD_BOSS_ASSET, WORLD_BOSS_SCALE, type BossModelState } from "./WorldBoss.js";
import { activeIncoming, WORLD_ZOOMS, worldCameraFraming, worldZoomAt, type WorldCaption, type WorldGroundBounds, type WorldZoom } from "./presentation.js";

export interface WorldAnchor {
  key: string;
  x: number;
  y: number;
  visible: boolean;
  depth: number;
}

export interface WorldSceneCallbacks {
  onTap(x: number, y: number): void;
  /** The camera target drifted far enough from the last load centre. */
  onMove(x: number, y: number): void;
  onZoom?(zoom: WorldZoom): void;
  onMarchState?(id: string, state: MarchModelState): void;
  onMarchTap?(id: string): void;
  onBossState?(state: BossModelState): void;
}

const TAP_PX = 8;
const TAP_MS = 450;
const LOAD_STEP = 6;
const ENCLAVE_SCALE = 0.22;
const MIN_DISTANCE = 12;
const MAX_DISTANCE = 110;

/** Model per tile kind; nodes take a level bucket (1/4/8), enclaves the HQ tier of the Command Nexus. */
export function markerModel(t: WorldTile): { id: string; variant?: number | string; scale: number; lod: 0 | 1 } {
  switch (t.kind) {
    case "node":
      return { id: `node_${t.node ?? "depot"}.l${t.level >= 8 ? 8 : t.level >= 4 ? 4 : 1}`, scale: 1, lod: 0 };
    case "pack":
      return { id: "zmb.shambler", variant: "fresh", scale: 0.46, lod: 1 };
    case "elite":
      return { id: "mark_elite", scale: 1, lod: 0 };
    case "hive":
      return { id: "hive", scale: 1, lod: 0 };
    case "boss":
      return { id: WORLD_BOSS_ASSET, variant: "fresh", scale: WORLD_BOSS_SCALE, lod: 0 };
    case "ruin":
      return { id: "ruin", scale: 1, lod: 0 };
    case "camp":
      return { id: "camp", scale: 1, lod: 0 };
    case "vault":
      return { id: "vault", scale: 1, lod: 0 };
    case "citadel":
      return { id: "citadel", scale: 2.5, lod: 0 };
    case "enclave":
      return { id: "bld.command_nexus", variant: buildingTier(t.owner?.hq ?? 1), scale: ENCLAVE_SCALE, lod: 1 };
    default:
      return { id: "mark_pack", scale: 1, lod: 0 };
  }
}

/**
 * The world map on the shared renderer (50-world.md R40–R44): instanced ring terrain for the loaded view,
 * fog planes over unexplored chunks, one instanced batch per marker model, march columns moving along their
 * lines in server time, own-base ring, selection ring, top-down orbit camera and tap picking.
 */
export class WorldScene {
  readonly root = new THREE.Group();
  private readonly landscape = new WorldTerrain();
  private selectedMarch: string | null = null;
  private readonly markers = new Map<string, InstancedModel>();
  private fog: THREE.InstancedMesh | null = null;
  private readonly fogMat = new THREE.MeshBasicMaterial({ color: 0x03050a, transparent: true, opacity: 0.78, depthWrite: false });
  private readonly marchers = new Map<string, { column: MarchColumn; line: THREE.Line; trail: MarchTrail; march: MarchView }>();
  private readonly selection: THREE.Mesh;
  private readonly ownRing: THREE.Mesh;
  private readonly fx = new WorldFx();
  private readonly boss: WorldBoss;
  private zoom: WorldZoom = "tactical";
  private reducedMotion = false;
  private view: WorldView | null = null;
  private me: WorldMe | null = null;
  private tiles: WorldTile[] = [];
  private lastLoad = { x: -1000, y: -1000 };
  private lastMoveAt = 0;
  private savedCamera: { position: THREE.Vector3; target: THREE.Vector3; view: THREE.PerspectiveCamera["view"]; fog: [number, number]; minDistance: number; maxDistance: number; minPolarAngle: number; maxPolarAngle: number; enablePan: boolean; screenSpacePanning: boolean; groundVisible: boolean } | null = null;
  private framingKey = "";
  private framingScale = 1;
  /** A jump requested before the camera was taken (chat coordinates, profile) wins over the home position. */
  private requestedJump: { x: number; y: number } | null = null;
  private readonly raycaster = new THREE.Raycaster();
  private readonly ground = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private readonly offFrame: () => void;
  private down: { x: number; y: number; t: number } | null = null;
  private time = 0;
  private disposed = false;
  private hidden = new Set<string>();
  private readonly canvas: HTMLElement;

  constructor(
    private readonly handle: SceneHandle,
    private readonly callbacks: WorldSceneCallbacks,
  ) {
    this.canvas = handle.renderer.domElement;
    this.boss = new WorldBoss((state) => callbacks.onBossState?.(state));
    this.root.name = "world";
    this.root.userData["zoom"] = this.zoom;
    this.root.visible = false;
    const selMat = new THREE.MeshStandardMaterial({ color: 0x3a2a05, emissive: new THREE.Color(0xf5c542), emissiveIntensity: 2.4, transparent: true, opacity: 0.95 });
    this.selection = new THREE.Mesh(new THREE.TorusGeometry(0.62, 0.05, 8, 40), selMat);
    this.selection.rotation.x = Math.PI / 2;
    this.selection.position.y = 0.28;
    this.selection.visible = false;
    this.ownRing = new THREE.Mesh(new THREE.TorusGeometry(0.85, 0.05, 8, 48), new THREE.MeshStandardMaterial({ color: 0x06222a, emissive: new THREE.Color(0x22d3ee), emissiveIntensity: 2.2 }));
    this.ownRing.rotation.x = Math.PI / 2;
    this.ownRing.position.y = 0.26;
    this.ownRing.visible = false;
    this.root.add(this.landscape.group);
    this.root.add(new THREE.HemisphereLight(0x9fb3d1, 0x3a3a44, 0.85));
    this.root.add(this.selection, this.ownRing, this.fx.group, this.boss.group);
    handle.scene.add(this.root);
    this.offFrame = handle.onFrame((dt) => this.tick(dt));
    this.canvas.addEventListener("pointerdown", this.onPointerDown, { capture: true });
    this.canvas.addEventListener("pointerup", this.onPointerUp, { capture: true });
    this.canvas.addEventListener("pointercancel", this.onPointerCancel, { capture: true });
  }

  // ─── camera ───

  /** Switches the shared camera to the top-down map orbit; the base ground is hidden meanwhile. */
  takeCamera(at: { x: number; y: number }): void {
    const c = this.handle.controls;
    const fog = this.handle.scene.fog as THREE.Fog | null;
    if (this.savedCamera || this.disposed) return;
    this.savedCamera = { position: this.handle.camera.position.clone(), target: c.target.clone(), view: this.handle.camera.view ? { ...this.handle.camera.view } : null, fog: fog ? [fog.near, fog.far] : [60, 160], minDistance: c.minDistance, maxDistance: c.maxDistance, minPolarAngle: c.minPolarAngle, maxPolarAngle: c.maxPolarAngle, enablePan: c.enablePan, screenSpacePanning: c.screenSpacePanning, groundVisible: this.handle.scene.getObjectByName("ground")?.visible ?? true };
    this.updateFraming();
    c.minDistance = MIN_DISTANCE * this.zoomScale();
    c.maxDistance = MAX_DISTANCE;
    c.minPolarAngle = 0.25;
    c.maxPolarAngle = 1.0;
    c.enablePan = true;
    c.screenSpacePanning = false;
    if (fog) {
      fog.near = 140;
      fog.far = 320;
    }
    this.setGroundVisible(false);
    this.root.visible = true;
    const target = this.requestedJump ?? at;
    this.requestedJump = null;
    this.jumpTo(target.x, target.y, WORLD_ZOOMS[1].distance * this.zoomScale());
  }

  releaseCamera(): void {
    this.root.visible = false;
    if (!this.savedCamera) return;
    this.setGroundVisible(this.savedCamera.groundVisible);
    const c = this.handle.controls;
    const fog = this.handle.scene.fog as THREE.Fog | null;
    if (fog) [fog.near, fog.far] = this.savedCamera.fog;
    c.target.copy(this.savedCamera.target);
    this.handle.camera.position.copy(this.savedCamera.position);
    this.handle.camera.view = this.savedCamera.view ? { ...this.savedCamera.view } : null;
    this.handle.camera.updateProjectionMatrix();
    c.minDistance = this.savedCamera.minDistance;
    c.maxDistance = this.savedCamera.maxDistance;
    c.minPolarAngle = this.savedCamera.minPolarAngle;
    c.maxPolarAngle = this.savedCamera.maxPolarAngle;
    c.enablePan = this.savedCamera.enablePan;
    c.screenSpacePanning = this.savedCamera.screenSpacePanning;
    c.update();
    this.savedCamera = null;
    this.framingKey = "";
  }

  private setGroundVisible(on: boolean): void {
    const g = this.handle.scene.getObjectByName("ground");
    if (g) g.visible = on;
  }

  /** Moves the orbit target to a tile, keeping the current zoom (or the given distance). */
  jumpTo(x: number, y: number, distance?: number): void {
    if (!this.savedCamera) this.requestedJump = { x, y };
    const c = this.handle.controls;
    const offset = this.handle.camera.position.clone().sub(c.target);
    if (distance !== undefined) offset.set(0, distance * 0.82, distance * 0.57);
    c.target.set(x, 0, y);
    this.handle.camera.position.copy(c.target).add(offset);
    c.update();
    this.lastLoad = this.centre();
    this.callbacks.onMove(this.lastLoad.x, this.lastLoad.y);
  }

  centre(): { x: number; y: number } {
    const t = this.handle.controls.target;
    return { x: Math.max(0, Math.min(WORLD_SIZE - 1, Math.round(t.x))), y: Math.max(0, Math.min(WORLD_SIZE - 1, Math.round(t.z))) };
  }

  cameraDistance(): number {
    return this.handle.camera.position.distanceTo(this.handle.controls.target);
  }

  setZoom(zoom: WorldZoom): void {
    this.updateFraming();
    const preset = WORLD_ZOOMS.find((z) => z.id === zoom)!;
    const c = this.handle.controls;
    const offset = this.handle.camera.position.clone().sub(c.target).normalize().multiplyScalar(Math.min(MAX_DISTANCE, preset.distance * this.zoomScale()));
    this.handle.camera.position.copy(c.target).add(offset);
    c.update();
    this.updateZoom();
  }

  retryBoss(): void { this.boss.retry(); }
  setReducedMotion(value: boolean): void { this.reducedMotion = value; }

  /** Actual ground coverage, used by the minimap independently of the API's fixed loaded radius. */
  groundBounds(): WorldGroundBounds | null {
    if (!this.root.visible) return null;
    const bounds = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
    const hit = new THREE.Vector3();
    this.handle.camera.updateMatrixWorld();
    for (const x of [-1, 1]) for (const y of [-1, 1]) {
      this.raycaster.setFromCamera(new THREE.Vector2(x, y), this.handle.camera);
      if (!this.raycaster.ray.intersectPlane(this.ground, hit)) return null;
      bounds.minX = Math.min(bounds.minX, hit.x); bounds.maxX = Math.max(bounds.maxX, hit.x);
      bounds.minY = Math.min(bounds.minY, hit.z); bounds.maxY = Math.max(bounds.maxY, hit.z);
    }
    return bounds;
  }

  /** The optical centre uses the free map area; orbit targets and picking retain real coordinates. */
  private updateFraming(): void {
    if (!this.savedCamera) return;
    const camera = this.handle.camera;
    const width = this.canvas.clientWidth || innerWidth;
    const height = this.canvas.clientHeight || width / camera.aspect;
    const key = `${width},${height}`;
    if (key === this.framingKey) return;
    const framing = worldCameraFraming({ width, height });
    if (this.framingKey) {
      const c = this.handle.controls;
      const offset = camera.position.clone().sub(c.target).multiplyScalar(framing.scale / this.framingScale);
      offset.clampLength(MIN_DISTANCE * framing.scale, MAX_DISTANCE);
      camera.position.copy(c.target).add(offset);
    }
    this.framingKey = key; this.framingScale = framing.scale;
    if (framing.offsetX) camera.setViewOffset(width, height, framing.offsetX, 0, width, height);
    else camera.clearViewOffset();
    this.handle.controls.minDistance = MIN_DISTANCE * this.framingScale;
    this.handle.controls.update();
  }

  private zoomScale(): number { return this.framingScale; }

  // ─── content ───

  setHidden(hidden: Set<string>): void {
    this.hidden = hidden;
    if (this.view) { this.rebuildMarkers(); this.syncThreats(); }
  }

  setView(view: WorldView, me: WorldMe | null): void {
    this.view = view;
    this.me = me;
    this.tiles = view.tiles;
    this.rebuildTerrain();
    this.rebuildFog();
    this.rebuildMarkers();
    this.updateOwn();
    this.syncThreats();
  }

  setMe(me: WorldMe | null): void {
    this.me = me;
    this.updateOwn();
    this.syncThreats();
    this.syncMarches(me?.marches ?? []);
  }

  setSelection(sel: { x: number; y: number } | null): void {
    this.selection.visible = sel !== null;
    if (sel) this.selection.position.set(sel.x, 0.28, sel.y);
  }

  private rebuildTerrain(): void {
    if (this.view) this.landscape.setView(this.view);
  }

  private rebuildFog(): void {
    if (!this.view) return;
    if (this.fog) {
      this.root.remove(this.fog);
      this.fog.dispose();
      this.fog.geometry.dispose();
      this.fog = null;
    }
    const explored = new Set(this.view.exploredChunks);
    const chunk = this.view.chunk;
    const { centre, radius } = this.view;
    const c0x = Math.floor(Math.max(0, centre.x - radius) / chunk);
    const c1x = Math.floor(Math.min(this.view.size - 1, centre.x + radius) / chunk);
    const c0y = Math.floor(Math.max(0, centre.y - radius) / chunk);
    const c1y = Math.floor(Math.min(this.view.size - 1, centre.y + radius) / chunk);
    const cells: [number, number][] = [];
    for (let cy = c0y; cy <= c1y; cy++) for (let cx = c0x; cx <= c1x; cx++) if (!explored.has(`${cx},${cy}`)) cells.push([cx, cy]);
    if (cells.length === 0) return;
    const geo = new THREE.PlaneGeometry(chunk, chunk);
    geo.rotateX(-Math.PI / 2);
    const mesh = new THREE.InstancedMesh(geo, this.fogMat, cells.length);
    const m = new THREE.Matrix4();
    cells.forEach(([cx, cy], i) => mesh.setMatrixAt(i, m.makeTranslation(cx * chunk + chunk / 2 - 0.5, 0.42, cy * chunk + chunk / 2 - 0.5)));
    mesh.instanceMatrix.needsUpdate = true;
    mesh.renderOrder = 5;
    this.fog = mesh;
    this.root.add(mesh);
  }

  private rebuildMarkers(): void {
    const wanted = new Map<string, { spec: ReturnType<typeof markerModel>; list: THREE.Matrix4[] }>();
    const pos = new THREE.Vector3();
    const rot = new THREE.Quaternion();
    const scl = new THREE.Vector3();
    for (const t of this.tiles) {
      if (this.hidden.has(t.kind)) continue;
      // The Titan needs its real animation mixer; static marker instancing would discard its idle clip.
      if (t.kind === "boss") continue;
      const spec = markerModel(t);
      if (this.zoom === "overview") spec.lod = 1;
      else if (this.zoom === "near" && t.kind === "enclave") spec.lod = 0;
      const key = `${spec.id}|${spec.variant ?? ""}|${spec.lod}`;
      const entry = wanted.get(key) ?? { spec, list: [] };
      const rotY = t.kind === "enclave" || t.kind === "citadel" ? 0 : ((t.x * 31 + t.y * 17) % 4) * (Math.PI / 2);
      pos.set(t.x, 0.02, t.y);
      rot.setFromAxisAngle(new THREE.Vector3(0, 1, 0), rotY);
      scl.setScalar(spec.scale);
      entry.list.push(new THREE.Matrix4().compose(pos, rot, scl));
      wanted.set(key, entry);
    }
    for (const [key, inst] of this.markers) {
      if (!wanted.has(key)) {
        this.root.remove(inst.group);
        inst.dispose();
        this.markers.delete(key);
      }
    }
    for (const [key, { spec, list }] of wanted) {
      let inst = this.markers.get(key);
      if (!inst) {
        inst = new InstancedModel(spec.id, spec.variant, spec.lod);
        inst.group.name = key;
        this.markers.set(key, inst);
        this.root.add(inst.group);
      }
      inst.setTransforms(list);
    }
  }

  private updateOwn(): void {
    const me = this.me;
    this.ownRing.visible = me !== null;
    if (!me) return;
    this.ownRing.position.set(me.position.x, 0.26, me.position.y);
    (this.ownRing.material as THREE.MeshStandardMaterial).emissive.set(Math.max(me.shieldUntil ?? 0, me.newbieUntil ?? 0) > serverNow() ? 0x22d3ee : 0xf5c542);
  }

  private syncThreats(): void {
    const me = this.me, now = serverNow();
    const ownKey = me ? `${me.position.x},${me.position.y}` : null;
    const sites = this.hidden.has("enclave") ? [] : this.tiles.filter((t) => t.live && t.owner?.burning && `${t.x},${t.y}` !== ownKey).map((t) => ({ key: t.owner!.id, x: t.x, y: t.y, until: null as number | null }));
    const centre = this.centre();
    sites.sort((a, b) => Math.hypot(a.x - centre.x, a.y - centre.y) - Math.hypot(b.x - centre.x, b.y - centre.y));
    if (me && (me.burningUntil ?? 0) > now) sites.unshift({ key: "own", ...me.position, until: me.burningUntil });
    this.fx.setFires(sites);
    const boss = this.hidden.has("boss") ? null : this.tiles.find((t) => t.kind === "boss" && t.boss && t.boss.windowUntil > now);
    this.boss.setTarget(boss?.boss ? { x: boss.x, y: boss.y, until: boss.boss.windowUntil } : null);
  }

  // ─── marches ───

  private syncMarches(marches: MarchView[]): void {
    const active = marches.filter((m) => m.state !== "done" && m.action !== "scout");
    const ids = new Set(active.map((m) => m.id));
    for (const [id, entry] of this.marchers) {
      if (ids.has(id)) continue;
      this.root.remove(entry.line);
      entry.line.geometry.dispose();
      (entry.line.material as THREE.Material).dispose();
      entry.trail.dispose();
      entry.column.dispose();
      this.marchers.delete(id);
    }
    for (const m of active) {
      const existing = this.marchers.get(m.id);
      if (existing) {
        existing.march = m;
        existing.column.setMembers(m.squad?.members ?? []);
        existing.trail.setCosmetic(m.trail ?? null);
        this.updateLine(existing.line, m);
        continue;
      }
      const geo = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3()]);
      const line = new THREE.Line(geo, new THREE.LineBasicMaterial({ color: m.action === "attack" ? 0xef4444 : 0x22d3ee, transparent: true, opacity: 0.85 }));
      line.renderOrder = 6;
      this.updateLine(line, m);
      this.root.add(line);
      const trail = new MarchTrail(m.trail ?? null);
      this.root.add(trail.group);
      const column = new MarchColumn((state) => this.callbacks.onMarchState?.(m.id, state));
      column.group.userData["marchId"] = m.id;
      const entry = { column, line, trail, march: m };
      this.marchers.set(m.id, entry); this.root.add(column.group);
      column.setMembers(m.squad?.members ?? []);
    }
  }

  private legOf(m: MarchView): { from: THREE.Vector3; to: THREE.Vector3; t: number } {
    const leg = marchLeg(m, this.me?.position ?? m.from, serverNow());
    return { from: new THREE.Vector3(leg.from.x, 0, leg.from.y), to: new THREE.Vector3(leg.to.x, 0, leg.to.y), t: leg.progress };
  }

  selectMarch(id: string | null): void { this.selectedMarch = id; }
  retryMarch(id: string): void { this.marchers.get(id)?.column.retry(); }

  private updateLine(line: THREE.Line, m: MarchView): void {
    const leg = this.legOf(m);
    const pos = line.geometry.getAttribute("position") as THREE.BufferAttribute;
    pos.setXYZ(0, leg.from.x, 0.2, leg.from.z);
    pos.setXYZ(1, leg.to.x, 0.2, leg.to.z);
    pos.needsUpdate = true;
  }

  /** Current world position of a march column (for labels). */
  marchPosition(m: MarchView): { x: number; y: number } {
    const leg = this.legOf(m);
    return { x: THREE.MathUtils.lerp(leg.from.x, leg.to.x, leg.t), y: THREE.MathUtils.lerp(leg.from.z, leg.to.z, leg.t) };
  }

  // ─── frame ───

  private tick(dt: number): void {
    if (!this.root.visible) return;
    this.updateFraming();
    this.time += dt;
    const reducedMotion = this.reducedMotion || (useSession.getState().settings?.reducedMotion ?? false);
    const now = serverNow();
    this.selection.rotation.z = reducedMotion ? 0 : this.time * 0.8;
    this.selection.scale.setScalar(reducedMotion ? 1 : 1 + Math.sin(this.time * 3) * 0.05);
    this.updateOwn();
    const incoming = activeIncoming(this.me?.incoming ?? [], now);
    this.fx.setIncoming(this.me && incoming.length ? { ...this.me.position, count: incoming.length, arrivesAt: Math.min(...incoming.map((a) => a.arrivesAt)) } : null);
    this.fx.update(now, reducedMotion);
    this.boss.update(dt, now, reducedMotion);
    for (const entry of this.marchers.values()) {
      const leg = this.legOf(entry.march);
      entry.trail.update(leg.from, leg.to, leg.t, reducedMotion);
      entry.column.update(leg.from, leg.to, leg.t, dt, reducedMotion, entry.march.id === this.selectedMarch);
    }
    // Camera drift → load more map; zoom → terrain LOD.
    const c = this.centre();
    this.updateZoom();
    if (Math.hypot(c.x - this.lastLoad.x, c.y - this.lastLoad.y) >= LOAD_STEP && this.time - this.lastMoveAt > 0.35) {
      this.lastLoad = c;
      this.lastMoveAt = this.time;
      this.callbacks.onMove(c.x, c.y);
    }
  }

  private updateZoom(): void {
    const zoom = worldZoomAt(this.cameraDistance() / this.zoomScale(), this.zoom);
    if (zoom !== this.zoom) {
      this.zoom = zoom;
      this.root.userData["zoom"] = zoom;
      this.callbacks.onZoom?.(zoom);
      this.rebuildMarkers();
    }
    this.landscape.setOverview(zoom === "overview");
  }

  /** Critical captions exclude current real boss and march mesh bounds, including animated rigid parts. */
  captionObstacles(): WorldCaption[] {
    if (!this.root.visible) return [];
    const actors: { key: string; actor: THREE.Object3D }[] = [];
    const boss = this.boss.group.getObjectByName("world-boss-model");
    if (this.boss.group.visible && boss) actors.push({ key: "boss-model", actor: boss });
    for (const [id, entry] of this.marchers) {
      const actor = entry.column.group.getObjectByName("march-unit-models");
      if (entry.column.group.visible && actor) actors.push({ key: `march-model:${id}`, actor });
    }
    const width = this.canvas.clientWidth || innerWidth;
    const height = this.canvas.clientHeight || width / this.handle.camera.aspect;
    const point = new THREE.Vector3();
    return actors.flatMap(({ key, actor }) => {
      const bounds = { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity };
      actor.updateWorldMatrix(true, true);
      actor.traverseVisible((object) => {
        if (!(object instanceof THREE.Mesh)) return;
        if (!object.geometry.boundingBox) object.geometry.computeBoundingBox();
        const box = object.geometry.boundingBox;
        if (!box) return;
        for (const x of [box.min.x, box.max.x]) for (const y of [box.min.y, box.max.y]) for (const z of [box.min.z, box.max.z]) {
          point.set(x, y, z).applyMatrix4(object.matrixWorld).project(this.handle.camera);
          if (point.z < -1 || point.z > 1) continue;
          const sx = (point.x + 1) * width / 2, sy = (1 - point.y) * height / 2;
          bounds.left = Math.min(bounds.left, sx); bounds.right = Math.max(bounds.right, sx);
          bounds.top = Math.min(bounds.top, sy); bounds.bottom = Math.max(bounds.bottom, sy);
        }
      });
      if (!Object.values(bounds).every(Number.isFinite) || bounds.right <= 0 || bounds.left >= width || bounds.bottom <= 0 || bounds.top >= height) return [];
      return [{ key, x: (bounds.left + bounds.right) / 2, y: bounds.bottom, width: bounds.right - bounds.left, height: bounds.bottom - bounds.top }];
    });
  }

  /** Screen anchors for HTML labels: enclaves, marches and the selection. */
  anchors(): WorldAnchor[] {
    const out: WorldAnchor[] = [];
    if (!this.root.visible) return out;
    const rect = this.canvas.getBoundingClientRect();
    const v = new THREE.Vector3();
    const project = (key: string, x: number, y: number, h: number) => {
      v.set(x, h, y).project(this.handle.camera);
      const visible = v.z > -1 && v.z < 1 && Math.abs(v.x) <= 0.94 && Math.abs(v.y) <= 0.92;
      out.push({ key, x: ((v.x + 1) / 2) * rect.width, y: ((1 - v.y) / 2) * rect.height, visible, depth: v.z });
    };
    const ownId = useSession.getState().account?.id;
    for (const t of this.tiles) if (t.kind === "enclave" && t.owner && !this.hidden.has("enclave")) {
      project(`enclave:${t.owner.id}`, t.x, t.y, 1.5);
      if (this.zoom === "overview" && t.owner.id !== ownId) out[out.length - 1]!.visible = false;
    }
    for (const t of this.tiles) if (t.kind === "boss" && t.boss && !this.hidden.has("boss")) project(`boss:${t.x},${t.y}`, t.x, t.y, 3.95);
    if (this.me && activeIncoming(this.me.incoming, serverNow()).length) project("incoming:own", this.me.position.x, this.me.position.y, 2.6);
    for (const entry of this.marchers.values()) {
      const p = this.marchPosition(entry.march);
      project(`march:${entry.march.id}`, p.x, p.y, 1.65);
    }
    return out;
  }

  // ─── picking ───

  private onPointerDown = (e: PointerEvent) => {
    if (!this.root.visible || !e.isPrimary) return;
    this.down = { x: e.clientX, y: e.clientY, t: performance.now() };
  };

  private onPointerCancel = () => {
    this.down = null;
  };

  private onPointerUp = (e: PointerEvent) => {
    const d = this.down;
    this.down = null;
    if (!d || !this.root.visible || !e.isPrimary) return;
    if (Math.hypot(e.clientX - d.x, e.clientY - d.y) > TAP_PX || performance.now() - d.t > TAP_MS) return;
    const rect = this.canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2(((e.clientX - rect.left) / rect.width) * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1);
    this.raycaster.setFromCamera(ndc, this.handle.camera);
    const hitMarch = this.raycaster.intersectObjects([...this.marchers.values()].map((entry) => entry.column.group), true)[0];
    if (hitMarch) {
      let object: THREE.Object3D | null = hitMarch.object;
      while (object && !object.userData["marchId"]) object = object.parent;
      if (object) { this.callbacks.onMarchTap?.(object.userData["marchId"] as string); return; }
    }
    const hit = new THREE.Vector3();
    if (!this.raycaster.ray.intersectPlane(this.ground, hit)) return;
    this.callbacks.onTap(Math.round(hit.x), Math.round(hit.z));
  };

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.offFrame();
    this.canvas.removeEventListener("pointerdown", this.onPointerDown, { capture: true });
    this.canvas.removeEventListener("pointerup", this.onPointerUp, { capture: true });
    this.canvas.removeEventListener("pointercancel", this.onPointerCancel, { capture: true });
    this.releaseCamera();
    for (const inst of this.markers.values()) inst.dispose();
    for (const entry of this.marchers.values()) {
      entry.line.geometry.dispose(); (entry.line.material as THREE.Material).dispose(); entry.trail.dispose();
      entry.column.dispose();
    }
    this.fog?.dispose();
    this.fog?.geometry.dispose(); this.fogMat.dispose();
    this.fx.dispose(); this.boss.dispose();
    for (const mesh of [this.selection, this.ownRing]) {
      mesh.geometry.dispose(); (mesh.material as THREE.Material).dispose();
    }
    this.landscape.dispose();
    this.markers.clear(); this.marchers.clear();
    this.handle.scene.remove(this.root);
    this.root.clear();
  }
}
